import { useState, useEffect, useCallback } from 'react';

export interface ForemanAreaNewWorkIdData {
  foremanArea: {
    id: number;
    name: string;
    serviceArea?: { id: number; name: string; code?: string };
  };
  newWorkIdCount: number;
  approvedWorkIdCount: number;
}

export interface NewWorkIdCountsFilter {
  foremanAreaId?: number;
  serviceAreaId?: number;
}

interface UseNewWorkIdCountsReturn {
  data: ForemanAreaNewWorkIdData[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  lastUpdated: Date | null;
}

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Custom hook to fetch NEW work ID counts grouped by foreman area.
 * When filter is provided (e.g. from home page global filter), only data for that area is fetched.
 *
 * @param filter - Optional { foremanAreaId, serviceAreaId } to scope the request (server-side filter).
 * @returns Object containing data, loading state, error, refetch function, and lastUpdated timestamp
 */
export function useNewWorkIdCounts(
  filter?: NewWorkIdCountsFilter | null
): UseNewWorkIdCountsReturn {
  const [data, setData] = useState<ForemanAreaNewWorkIdData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (filter?.foremanAreaId != null) {
        params.append('foremanAreaId', String(filter.foremanAreaId));
      }
      if (filter?.serviceAreaId != null) {
        params.append('serviceAreaId', String(filter.serviceAreaId));
      }
      const queryString = params.toString();
      const url = `${BASE_URL}/work-ids/new-counts-by-foreman-area${queryString ? `?${queryString}` : ''}`;
      const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch new work ID counts: ${response.statusText}`);
      }
      const json = await response.json();
      setData(json.data ?? []);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch new work ID counts'));
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [filter?.foremanAreaId, filter?.serviceAreaId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    lastUpdated,
  };
}
